import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, fmtMoney, fmtTime, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Clock, ClipboardList, CheckCircle2, Calendar, Receipt, Coffee, LogOut, ArrowRight, Wrench, Trash2, Pencil, ClipboardCheck, ShieldAlert } from "lucide-react";
import ShopTimeDialog from "@/components/ShopTimeDialog";
import TruckInspectionDialog from "@/components/TruckInspectionDialog";
import BenefitsBalancesCard from "@/pages/employee/BenefitsBalancesCard";
import { toast } from "sonner";

const STATUS_META = {
  not_clocked_in: { label: "Not Clocked In", color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  working: { label: "Working", color: "bg-green-50 text-green-700", dot: "bg-green-500 animate-pulse" },
  on_lunch: { label: "On Lunch", color: "bg-amber-50 text-amber-700", dot: "bg-amber-500 animate-pulse" },
  clocked_out: { label: "Clocked Out", color: "bg-slate-100 text-slate-700", dot: "bg-slate-400" },
  submitted: { label: "Day Submitted", color: "bg-sky-50 text-sky-700", dot: "bg-sky-500" },
  approved: { label: "Approved", color: "bg-green-50 text-green-700", dot: "bg-green-500" },
  rejected: { label: "Rejected — Edit & Resubmit", color: "bg-red-50 text-red-700", dot: "bg-red-500" },
};

export default function EmployeeDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopOpen, setShopOpen] = useState(false);
  const [shopEditing, setShopEditing] = useState(null);
  const [inspectionOpen, setInspectionOpen] = useState(false);

  const commissionEligible = user?.default_pay_method && user.default_pay_method !== "hourly";
  const isLeadTech = !!user?.is_lead_technician;

  const load = async () => {
    const [s, sum] = await Promise.all([
      api.get("/time/status").then((r) => r.data),
      api.get("/daily/summary").then((r) => r.data),
    ]);
    setStatus(s);
    setSummary(sum);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deleteShopTime = async (id) => {
    if (!confirm("Delete this shop time entry?")) return;
    try { await api.delete(`/shop-time/${id}`); await load(); toast.success("Removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  if (loading) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  const meta = STATUS_META[status?.status] || STATUS_META.not_clocked_in;
  const today = new Date().toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
  const missedClockouts = status?.missed_clock_outs || [];

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/time/missed-clockout/${alertId}/acknowledge`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="employee-dashboard">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">{today}</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">
          Hey, {user?.first_name}.
        </h1>
      </div>

      {/* Benefits balances — renders nothing when tenant has benefits off
          or when this employee has no benefit type enabled. */}
      <BenefitsBalancesCard />

      {/* Missed clock-out banner(s) */}
      {missedClockouts.map((a) => (
        <Card
          key={a.id}
          className="border-amber-300 bg-amber-50 rounded-2xl"
          data-testid={`missed-clockout-banner-${a.date}`}
        >
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldAlert className="h-6 w-6 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900 text-sm">
                You forgot to clock out on {new Date(a.date).toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" })}.
              </p>
              <p className="text-xs text-amber-800 mt-1">
                Your time was auto-closed at end of shift. Please let your admin know so they can adjust your hours.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => acknowledgeAlert(a.id)}
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
              data-testid={`dismiss-missed-clockout-${a.date}`}
            >
              Got it
            </Button>
          </CardContent>
        </Card>
      ))}

      {/* "Submit your day" reminder — shown after clock-out, before finalize */}
      {status?.status === "clocked_out" && (
        <Card className="border-2 border-green-400 bg-green-50 rounded-2xl shadow-sm" data-testid="submit-day-reminder">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-green-900 text-sm">One more step — submit your day for payroll.</p>
              <p className="text-xs text-green-800 mt-0.5">You clocked out at {fmtTime(status?.time_entry?.clock_out)}. Tap below to sign &amp; submit so your admin can approve your hours.</p>
            </div>
            <Button
              asChild
              className="bg-green-600 hover:bg-green-700 text-white font-bold shrink-0"
              data-testid="submit-day-cta"
            >
              <Link to="/finalize">Submit Day →</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Status card */}
      <Card className="border-slate-200 rounded-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-sky-600 to-sky-700 text-white p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-sky-100">Current Status</p>
              <p className="text-xl font-bold font-display mt-0.5">{meta.label}</p>
            </div>
            <span className={`h-3 w-3 rounded-full ${meta.dot}`} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-sky-100">Clock In</p>
              <p className="font-semibold">{fmtTime(status?.time_entry?.clock_in)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-sky-100">Clock Out</p>
              <p className="font-semibold">{fmtTime(status?.time_entry?.clock_out)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-sky-100">Net Hours</p>
              <p className="font-semibold">{summary?.hours?.net_hours?.toFixed(2) || "0.00"} h</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-sky-100">Est. Pay</p>
              <p className="font-semibold">{fmtMoney(summary?.pay?.estimated_final_pay)}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Primary actions */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          asChild
          className="h-20 bg-sky-600 hover:bg-sky-700 text-white rounded-2xl text-base font-bold flex-col gap-1 btn-press"
          data-testid="goto-time-clock-btn"
        >
          <Link to="/time">
            <Clock className="h-7 w-7" />
            Time Clock
          </Link>
        </Button>
        {commissionEligible && (
          <Button
            asChild
            className="h-20 bg-green-600 hover:bg-green-700 text-white rounded-2xl text-base font-bold flex-col gap-1 btn-press"
            data-testid="goto-jobs-btn"
          >
            <Link to="/jobs">
              <ClipboardList className="h-7 w-7" />
              Job Entries
            </Link>
          </Button>
        )}
        {isLeadTech && (
          <Button
            onClick={() => setInspectionOpen(true)}
            className={`h-20 rounded-2xl text-base font-bold flex-col gap-1 btn-press text-white ${
              summary?.truck_inspection ? "bg-emerald-600 hover:bg-emerald-700"
              : summary?.inspection_required_today ? "bg-red-600 hover:bg-red-700"
              : "bg-indigo-600 hover:bg-indigo-700"
            }`}
            data-testid="goto-inspection-btn"
          >
            {summary?.truck_inspection ? <CheckCircle2 className="h-7 w-7" /> : (summary?.inspection_required_today ? <ShieldAlert className="h-7 w-7" /> : <ClipboardCheck className="h-7 w-7" />)}
            <span className="leading-tight">
              {summary?.truck_inspection ? "Inspection ✓" : "Truck Pre-Inspection"}
            </span>
          </Button>
        )}
        <Button
          onClick={() => { setShopEditing(null); setShopOpen(true); }}
          className="h-20 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl text-base font-bold flex-col gap-1 btn-press"
          data-testid="add-shop-time-btn"
        >
          <Wrench className="h-7 w-7" />
          Add Shop Time
        </Button>
        <Button
          asChild
          variant={status?.status === "clocked_out" ? "default" : "outline"}
          className={
            status?.status === "clocked_out"
              ? "h-20 rounded-2xl text-base font-bold flex-col gap-1 btn-press bg-green-600 hover:bg-green-700 text-white animate-pulse-slow shadow-md"
              : "h-20 rounded-2xl text-base font-bold flex-col gap-1 btn-press border-slate-300"
          }
          data-testid="goto-finalize-btn"
        >
          <Link to="/finalize">
            <CheckCircle2 className={`h-7 w-7 ${status?.status === "clocked_out" ? "text-white" : "text-sky-700"}`} />
            {status?.status === "clocked_out" ? "Submit Day" : "Finalize Day"}
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="h-20 rounded-2xl text-base font-bold flex-col gap-1 btn-press border-slate-300"
          data-testid="goto-paystubs-btn"
        >
          <Link to="/paystubs">
            <Receipt className="h-7 w-7 text-sky-700" />
            Paystubs
          </Link>
        </Button>
      </div>

      {isLeadTech && summary?.inspection_required_today && !summary?.truck_inspection && (
        <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-900 flex items-start gap-2" data-testid="inspection-required-alert">
          <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold">Truck pre-inspection required today.</p>
            <p className="text-xs">You won't be able to finalize the day until you complete it.</p>
          </div>
        </div>
      )}

      {/* Shop time entries today */}
      {summary?.shop_time?.length > 0 && (
        <Card className="border-slate-200 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold font-display flex items-center justify-between">
              <span>Shop / Other Time Today</span>
              <span className="text-xs text-slate-500 font-normal">
                {summary.shop_hours_approved}h approved · {summary.shop_hours_pending}h pending
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {summary.shop_time.map((s) => (
              <div key={s.id} className="flex items-center justify-between border-b border-slate-100 last:border-0 pb-2 last:pb-0">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">{s.category} <span className="font-normal text-slate-500">· {Number(s.hours).toFixed(2)}h</span></p>
                  {s.notes && <p className="text-xs text-slate-500 truncate">{s.notes}</p>}
                  {s.status === "rejected" && s.admin_notes && (
                    <p className="text-xs text-red-600 truncate">Rejected: {s.admin_notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <ShopStatusBadge status={s.status} />
                  {s.status !== "approved" && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => { setShopEditing(s); setShopOpen(true); }} data-testid={`shop-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteShopTime(s.id)} data-testid={`shop-del-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <ShopTimeDialog open={shopOpen} onOpenChange={setShopOpen} onSaved={load} editing={shopEditing} />
      <TruckInspectionDialog open={inspectionOpen} onOpenChange={setInspectionOpen} onSaved={load} />

      {/* Today's production */}
      <Card className="border-slate-200 rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold font-display">Today's Production</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Row label="Gross Invoices" value={fmtMoney(summary?.production?.gross_invoice_total)} />
          <Row label="Allowable Deductions" value={fmtMoney(summary?.production?.allowable_deductions)} />
          <Row label="Net Commissionable" value={fmtMoney(summary?.production?.net_commissionable_revenue)} bold />
          <div className="border-t border-slate-200 my-2" />
          <Row label={`Commission (${summary?.pay?.commission_percentage || 0}%)`} value={fmtMoney(summary?.pay?.estimated_commission_pay)} />
          <Row label="Hourly Pay" value={fmtMoney(summary?.pay?.estimated_hourly_pay)} />
          <Row label="Estimated Final Pay" value={fmtMoney(summary?.pay?.estimated_final_pay)} highlight />
        </CardContent>
      </Card>

      <Button asChild variant="ghost" className="w-full text-sky-700 font-semibold" data-testid="goto-week-btn">
        <Link to="/week">View weekly summary <ArrowRight className="h-4 w-4 ml-1" /></Link>
      </Button>
    </div>
  );
}

function Row({ label, value, bold, highlight }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{label}</span>
      <span className={`${highlight ? "text-lg font-extrabold text-green-700" : bold ? "font-bold text-slate-900" : "font-medium text-slate-900"}`}>{value}</span>
    </div>
  );
}

function ShopStatusBadge({ status }) {
  const map = {
    pending: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status] || map.pending}`}>{status}</span>;
}
